/**
 * Filter Enhancement Utilities
 * 
 * This module provides enhanced filtering capabilities that work alongside
 * the existing filter system without breaking current functionality.
 */

import { Business, ActivityForHomepage, FilterMetadata, FilterPerformance } from '@/types/homepage';
import { EXPERIENCE_CATEGORIES } from '@/constants/experienceCategories';

// Cache for filter results to improve performance
class FilterResultCache {
  private cache: Map<string, {
    results: Business[];
    metadata: FilterMetadata;
  }> = new Map();
  
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  
  get(key: string): { results: Business[]; metadata: FilterMetadata } | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() - cached.metadata.lastUpdated > this.CACHE_TTL) {
      this.cache.delete(key);
      return null;
    }
    
    return cached;
  }
  
  set(key: string, results: Business[], metadata: FilterMetadata): void {
    this.cache.set(key, { results, metadata });
  }
  
  clear(): void {
    this.cache.clear();
  }
  
  size(): number {
    return this.cache.size;
  }
}

// Global cache instance
const filterCache = new FilterResultCache();

/**
 * Creates a cache key from filter parameters
 */
export function createFilterCacheKey(
  businessCount: number,
  searchQuery: string,
  activityFilters: { showAnimalActivities: boolean; showAdventureActivities: boolean },
  selectedAnimalTypes: string[],
  selectedAdventureTypes: string[],
  activeExperienceFilter: string | null,
  isExplorationMode: boolean,
  isLocationSearch?: boolean
): string {
  return JSON.stringify({
    count: businessCount,
    search: searchQuery.toLowerCase().trim(),
    activity: activityFilters,
    animals: selectedAnimalTypes.sort(),
    adventures: selectedAdventureTypes.sort(),
    experience: activeExperienceFilter,
    exploration: isExplorationMode,
    locationSearch: isLocationSearch || false
  });
}

/**
 * Enhanced experience category matching
 * 
 * This function provides better matching for experience categories
 * while maintaining backward compatibility with the existing string-based approach.
 */
export function matchesExperienceCategory(
  business: Business,
  activities: ActivityForHomepage[],
  categoryFilter: string
): boolean {
  // First try the existing approach for backward compatibility
  const legacyMatch = activities.some(activity => 
    activity.name.toLowerCase().includes(categoryFilter.toLowerCase()) ||
    activity.activityType?.toLowerCase().includes(categoryFilter.toLowerCase())
  );
  
  if (legacyMatch) return true;
  
  // Enhanced matching using category IDs
  const category = EXPERIENCE_CATEGORIES.find(cat => 
    cat.id === categoryFilter || 
    cat.name.toLowerCase() === categoryFilter.toLowerCase()
  );
  
  if (!category) return false;
  
  // Check if business has activities that match this category type
  const hasMatchingActivities = activities.some(activity => {
    // Check if activity has explicit category mapping
    if (activity.categoryId === category.id) return true;
    
    // Check if activity type matches category type
    if (category.type === 'both') return true;
    if (category.type === activity.activityType) return true;
    
    // Check tags if available
    if (activity.tags?.some(tag => 
      tag.toLowerCase().includes(category.name.toLowerCase()) ||
      category.name.toLowerCase().includes(tag.toLowerCase())
    )) return true;
    
    return false;
  });
  
  return hasMatchingActivities;
}

/**
 * Enhanced animal type matching
 */
export function matchesAnimalTypes(
  business: Business,
  activities: ActivityForHomepage[],
  selectedAnimalTypes: string[]
): boolean {
  if (selectedAnimalTypes.length === 0) return true;
  
  return selectedAnimalTypes.some(animalType => {
    return activities.some(activity => {
      // Existing approach
      if (activity.name.toLowerCase().includes(animalType.toLowerCase())) return true;
      
      // Enhanced approach with tags
      if (activity.tags?.some(tag => 
        tag.toLowerCase().includes(animalType.toLowerCase())
      )) return true;
      
      return false;
    });
  });
}

/**
 * Enhanced adventure type matching
 */
export function matchesAdventureTypes(
  business: Business,
  activities: ActivityForHomepage[],
  selectedAdventureTypes: string[]
): boolean {
  if (selectedAdventureTypes.length === 0) return true;
  
  return selectedAdventureTypes.some(adventureType => {
    return activities.some(activity => {
      // Existing approach
      if (activity.name.toLowerCase().includes(adventureType.toLowerCase())) return true;
      
      // Enhanced approach with tags
      if (activity.tags?.some(tag => 
        tag.toLowerCase().includes(adventureType.toLowerCase())
      )) return true;
      
      return false;
    });
  });
}

/**
 * Enhanced filtering function that works alongside existing system
 */
export function enhancedFilterBusinesses(
  businesses: Business[],
  businessActivities: Record<string, ActivityForHomepage[]>,
  filters: {
    searchQuery: string;
    isExplorationMode: boolean;
    isLocationSearch?: boolean;
    activityFilters: { showAnimalActivities: boolean; showAdventureActivities: boolean };
    selectedAnimalTypes: string[];
    selectedAdventureTypes?: string[];
    activeExperienceFilter: string | null;
  }
): {
  filteredBusinesses: Business[];
  performance: FilterPerformance;
  metadata: FilterMetadata;
} {
  const startTime = performance.now();
  const filterStartTime = Date.now();
  
  // Create cache key
  const cacheKey = createFilterCacheKey(
    businesses.length,
    filters.searchQuery,
    filters.activityFilters,
    filters.selectedAnimalTypes,
    filters.selectedAdventureTypes || [],
    filters.activeExperienceFilter,
    filters.isExplorationMode,
    filters.isLocationSearch
  );
  
  // Check cache first
  const cached = filterCache.get(cacheKey);
  if (cached) {
    return {
      filteredBusinesses: cached.results,
      performance: {
        cacheHit: true,
        filterDuration: performance.now() - startTime,
        businessesProcessed: businesses.length,
        filtersApplied: getAppliedFilters(filters)
      },
      metadata: cached.metadata
    };
  }
  
  // Apply filters (same logic as existing system but with enhancements)
  let filtered = businesses;
  const appliedFilters: string[] = [];
  
  // Activity type filters
  if (!(filters.activityFilters.showAnimalActivities && filters.activityFilters.showAdventureActivities)) {
    filtered = filtered.filter(business => {
      if (filters.activityFilters.showAnimalActivities && business.hasAnimalActivities) return true;
      if (filters.activityFilters.showAdventureActivities && business.hasAdventureActivities) return true;
      return false;
    });
    appliedFilters.push('activityType');
  }
  
  // Search query filter - skip if in exploration mode OR location search mode
  if (filters.searchQuery.trim() && !filters.isExplorationMode && !filters.isLocationSearch) {
    const query = filters.searchQuery.toLowerCase();
    filtered = filtered.filter(business => 
      business.name.toLowerCase().includes(query) || 
      business.description.toLowerCase().includes(query) ||
      business.location.address.city.toLowerCase().includes(query) ||
      business.location.address.country.toLowerCase().includes(query)
    );
    appliedFilters.push('search');
  }
  
  // Experience category filter (enhanced)
  if (filters.activeExperienceFilter) {
    filtered = filtered.filter(business => {
      const activities = businessActivities[business._id] || [];
      return matchesExperienceCategory(business, activities, filters.activeExperienceFilter!);
    });
    appliedFilters.push('experienceCategory');
  }
  
  // Animal type filters (enhanced)
  if (filters.selectedAnimalTypes.length > 0) {
    filtered = filtered.filter(business => {
      const activities = businessActivities[business._id] || [];
      return matchesAnimalTypes(business, activities, filters.selectedAnimalTypes);
    });
    appliedFilters.push('animalTypes');
  }
  
  // Adventure type filters (enhanced)
  if (filters.selectedAdventureTypes && filters.selectedAdventureTypes.length > 0) {
    filtered = filtered.filter(business => {
      const activities = businessActivities[business._id] || [];
      return matchesAdventureTypes(business, activities, filters.selectedAdventureTypes || []);
    });
    appliedFilters.push('adventureTypes');
  }
  
  const filterDuration = performance.now() - startTime;
  const metadata: FilterMetadata = {
    lastUpdated: Date.now(),
    appliedAt: filterStartTime,
    resultCount: filtered.length,
    filterTime: filterDuration,
    source: 'both'
  };
  
  // Cache results
  filterCache.set(cacheKey, filtered, metadata);
  
  return {
    filteredBusinesses: filtered,
    performance: {
      cacheHit: false,
      filterDuration,
      businessesProcessed: businesses.length,
      filtersApplied: appliedFilters
    },
    metadata
  };
}

function getAppliedFilters(filters: {
  searchQuery: string;
  isExplorationMode: boolean;
  isLocationSearch?: boolean;
  activityFilters: { showAnimalActivities: boolean; showAdventureActivities: boolean };
  selectedAnimalTypes: string[];
  selectedAdventureTypes?: string[];
  activeExperienceFilter: string | null;
}): string[] {
  const applied: string[] = [];
  
  if (!(filters.activityFilters.showAnimalActivities && filters.activityFilters.showAdventureActivities)) {
    applied.push('activityType');
  }
  if (filters.searchQuery.trim() && !filters.isExplorationMode && !filters.isLocationSearch) {
    applied.push('search');
  }
  if (filters.activeExperienceFilter) {
    applied.push('experienceCategory');
  }
  if (filters.selectedAnimalTypes.length > 0) {
    applied.push('animalTypes');
  }
  if (filters.selectedAdventureTypes && filters.selectedAdventureTypes.length > 0) {
    applied.push('adventureTypes');
  }
  
  return applied;
}

/**
 * Clear the filter cache
 */
export function clearFilterCache(): void {
  filterCache.clear();
}

/**
 * Get cache statistics
 */
export function getFilterCacheStats(): {
  size: number;
  hitRate?: number;
} {
  return {
    size: filterCache.size()
  };
} 